import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Polls /api/game/event/check on mount and surfaces any pending random
 * event in a full-screen modal. Mount once at the top of Command and
 * WorldMap. Self-contained — no props.
 */
export default function RandomEventModal() {
  const { auth, refreshState, battleTick } = useAuth();
  const [event, setEvent] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const check = useCallback(async () => {
    if (!auth) return;
    // Don't interrupt setup flows (new-game wizard, tutorial), public pages,
    // or the share-card view.
    const path = window.location.pathname || "";
    if (path.startsWith("/new-game")
        || path.startsWith("/tutorial")
        || path.startsWith("/auth")
        || path.startsWith("/card")
        || path === "/") return;
    try {
      const { data } = await api.get("/game/event/check");
      if (data.event) setEvent(data.event);
    } catch (e) {
      // ignore - non-blocking
    }
  }, [auth]);

  // Re-polls on auth mount AND after every refreshState (which Battle.jsx
  // calls after submitPersonal / resolveCommand / decideSally).
  useEffect(() => { check(); }, [check, battleTick]);

  // If the user logs out (or lands on public pages), drop any lingering
  // modal state so we don't overlay Landing / Auth / New Game.
  useEffect(() => {
    if (!auth) {
      setEvent(null);
      setOutcome(null);
      setResolving(false);
    }
  }, [auth]);

  const resolveChoice = async (choiceKey) => {
    if (!event) return;
    setResolving(true);
    try {
      const { data } = await api.post("/game/event/resolve", {
        event_id: event.id, choice: choiceKey,
      });
      setOutcome(data.result);
      await refreshState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resolve event.");
    } finally {
      setResolving(false);
    }
  };

  const close = async () => {
    setEvent(null);
    setOutcome(null);
    // Clear the server-side pending event so it doesn't reappear on the next poll
    try { await api.post("/game/event/dismiss"); } catch (e) { /* non-blocking */ }
  };

  if (!event) return null;

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center px-4 py-8"
         data-testid="random-event-modal"
         style={{ background: "rgba(2,2,3,0.85)", backdropFilter: "blur(6px)" }}>
      <div className="max-w-2xl w-full narrative-panel relative fade-up"
           style={{ borderColor: "var(--blood)", borderWidth: 1, boxShadow: "0 0 0 1px #2a0000, 0 24px 80px rgba(106,0,0,0.5)" }}>
        <button
          onClick={close}
          className="absolute top-3 right-3 text-muted-h hover:text-bone transition-colors"
          aria-label="Dismiss"
          data-testid="event-close-button"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-blood mt-0.5 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="tac-label" data-testid="event-type">{event.type.replace(/_/g, " ")}</p>
            <h2 className="font-serif-h text-3xl text-bone uppercase mt-1 leading-tight"
                data-testid="event-title">
              {event.title}
            </h2>
          </div>
        </div>

        <p className="text-base leading-relaxed text-[var(--text-main)] italic"
           data-testid="event-setup">
          {event.setup}
        </p>

        {!outcome ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {event.choices.map((c) => (
              <button
                key={c.key}
                disabled={resolving}
                onClick={() => resolveChoice(c.key)}
                className="tactical-card text-left p-4 hover:border-blood transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`event-choice-${c.key}`}
              >
                <p className="font-mono text-sm text-bone">{c.label}</p>
                <p className="text-[0.7rem] text-muted-h mt-2 leading-relaxed">{c.intel}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-6 fade-up">
            <div className="border-l-2 border-blood pl-4 py-2" data-testid="event-outcome">
              <p className="tac-label">OUTCOME</p>
              <p className="text-sm text-bone mt-2 italic leading-relaxed"
                 data-testid="event-outcome-narrative">
                {outcome.narrative}
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[0.7rem] font-mono text-muted-h">
                {outcome.gain > 0 && (
                  <span className="text-bone" data-testid="event-gain">+{outcome.gain} converted</span>
                )}
                {outcome.loss > 0 && (
                  <span className="text-blood" data-testid="event-loss">−{outcome.loss} fallen</span>
                )}
                {outcome.morale_delta !== undefined && outcome.morale_delta !== 0 && (
                  <span data-testid="event-morale-delta">
                    Enemy morale {outcome.morale_delta > 0 ? "+" : ""}{outcome.morale_delta}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={close}
              className="btn-blood mt-6 w-full"
              data-testid="event-continue-button"
            >
              {resolving ? <Loader className="w-3 h-3 animate-spin inline" /> : "CONTINUE"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
